import React, { useEffect, useState } from "react";
import { ArrowUpRight, ArrowDownLeft, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

type Transaction = {
  id: string;
  type: "deposit" | "withdraw" | "trade" | "p2p";
  token: string;
  amount: number;
  status: "pending" | "completed" | "failed";
  timestamp: string;
  from_address?: string;
  to_address?: string;
  network?: string;
};

interface HistorySummaryProps {
  className?: string;
  variant?: "compact" | "detailed";
  showSignature?: boolean;
}

export default function HistorySummary({
  className,
  variant = "compact",
  showSignature = false,
}: HistorySummaryProps) {
  const [latestTransaction, setLatestTransaction] = useState<Transaction | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      if (raw) setUser(JSON.parse(raw));
    } catch (e) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchLatestTransaction();

    // Set up listener for transaction updates
    const handleTransactionUpdate = () => {
      fetchLatestTransaction();
    };

    window.addEventListener("transactionUpdated", handleTransactionUpdate);
    return () => {
      window.removeEventListener("transactionUpdated", handleTransactionUpdate);
    };
  }, [user]);

  async function fetchLatestTransaction() {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/transactions/${user.id}`);
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      const transactions = data.transactions || [];
      if (transactions.length > 0) {
        setLatestTransaction(transactions[0]);
      }
    } catch (err) {
      // Silent fail for summary component
    } finally {
      setLoading(false);
    }
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case "deposit":
      case "p2p":
        return <ArrowDownLeft className="w-3 h-3 text-green-400" />;
      case "withdraw":
        return <ArrowUpRight className="w-3 h-3 text-red-400" />;
      case "trade":
        return <TrendingUp className="w-3 h-3 text-blue-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-400";
      case "pending":
        return "text-yellow-400";
      case "failed":
        return "text-red-400";
      default:
        return "text-gray-400";
    }
  };

  if (!user || loading) {
    return null;
  }

  if (!latestTransaction) {
    return (
      <div
        className={cn(
          "text-xs text-muted-foreground",
          variant === "detailed" && "p-2",
          className
        )}
      >
        No transactions yet
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "text-xs space-y-1",
          className
        )}
      >
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            {getTransactionIcon(latestTransaction.type)}
          </div>
          <span className="font-medium capitalize text-foreground">
            {latestTransaction.type}
          </span>
          <span className="text-muted-foreground">
            {latestTransaction.amount.toFixed(2)} {latestTransaction.token}
          </span>
        </div>
        <div className={cn("text-[10px]", getStatusColor(latestTransaction.status))}>
          {latestTransaction.status.toUpperCase()}
        </div>
        {showSignature && (
          <div className="text-[10px] text-muted-foreground pt-1 border-t border-muted mt-1">
            TX: {latestTransaction.id.slice(0, 8)}...
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("p-2 rounded-md bg-muted/30 text-xs space-y-2", className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
            {getTransactionIcon(latestTransaction.type)}
          </div>
          <span className="font-semibold capitalize">
            {latestTransaction.type}
          </span>
        </div>
        <span className={cn("font-semibold", getStatusColor(latestTransaction.status))}>
          {latestTransaction.status.toUpperCase()}
        </span>
      </div>
      <div className="flex justify-between text-muted-foreground">
        <span>
          {latestTransaction.type === "withdraw" ? "-" : "+"}
          {latestTransaction.amount.toFixed(4)} {latestTransaction.token}
        </span>
        <span>{new Date(latestTransaction.timestamp).toLocaleString()}</span>
      </div>
      {showSignature && (
        <div className="border-t border-muted pt-2 text-[10px] text-muted-foreground">
          <div>Signature: {latestTransaction.id.slice(0, 16)}...</div>
          <div className="mt-1">
            {new Date(latestTransaction.timestamp).toLocaleDateString()}
          </div>
        </div>
      )}
    </div>
  );
}
